"use strict";

/**
 * Control zur Verarbeitung von Tooltips für entsprechende Layer.
 *
 * @class
 * @extends gc.gis.control.GCAbstractMapControl
 * @memberof gc.gis.control
 * @author Christian Schmidt <c.schmidt@brain-scc.de>
 *
 * @param {Object} options Aufrufoptionen (siehe {@link gc.gis.control.GCAbstractMapControl})
 */
gc.gis.control.GCLayerTooltipManager = function(options)
{
	gc.gis.control.GCAbstractMapControl.call(this, options);

	/**
	 * @type {gc.gis.service.HtmlPopupService}
	 * @private
	 */
	this._service = options.serviceFactory.createHtmlPopupService();

	/**
	 * Information, die beim Lades des Tooltips angezeigt werden soll
	 * @type {string}
	 * @private
	 */
	this._loadInfo = '';

	/**
	 * @type {gc.gis.layer.GCLayer[]}
	 * @private
	 */
	this._layers = [];

	/**
	 * Flag, ob der Tooltip aktiv ist.
	 * @type {boolean}
	 * @private
	 */

	this._active = false;

	/**
	 * Flag, ob sich der Mauszeiger über der Karte befindet.
	 * @type {boolean}
	 * @private
	 */
	this._hoverMap = false;

	/**
	 * Anzahl der aktivierten Interaktionen => wenn > 0 dann kein Tooltip.
	 * @type {number}
	 * @private
	 */
	this._interactionCount = 0;

	/**
	 * Wartezeit, bis ein Request ausgelöst wird => solange muss der Nutzer auf einer Stelle mit der Maus verweilen.
	 * @type {number}
	 * @private
	 */
	this._delay = 500;

	/**
	 * Pixel, die der Nutzer mind. die Maus bewegen muss, damit einer neuer Request gestartet wird.
	 * @type {number}
	 * @private
	 */
	this._pixelThreshold = 2;

	/**
	 * Gestarteter Timer.
	 * @private
	 */
	this._timer = undefined;

	/**
	 * Aktuelle Mausposition innerhalb der Karte.
	 * @type {Array.<number>|undefined}
	 * @private
	 */
	this._pixel = undefined;
};
gc.gis.control.GCLayerTooltipManager.prototype = Object.create(gc.gis.control.GCAbstractMapControl.prototype);

/**
 * @inheritdoc
 */
gc.gis.control.GCLayerTooltipManager.prototype.getImpl = function()
{
	return undefined;
};

/**
 * @inheritdoc
 */
gc.gis.control.GCLayerTooltipManager.prototype.init = function()
{
	var self = this;

	// Maus überwachen
	this._map.on(gc.gis.map.GCMapEvent.MOUSE_LEAVE, this, function () {
		this._hoverMap = false;
		this._update();
	});
	this._map.on(gc.gis.map.GCMapEvent.MOUSE_ENTER, this, function () {
		this._hoverMap = true;
		this._update();
	});

	// Interaktionen überwachen
	this._map.on(gc.gis.map.GCMapEvent.INTERACTION_ACTIVATED, this, function () {
		self._interactionCount++;
		this._update();
	});
	this._map.on(gc.gis.map.GCMapEvent.INTERACTION_DEACTIVATED, this, function () {
		if (self._interactionCount > 0)
		{
			self._interactionCount--;
		}
		this._update();
	});

	// Layer-Überwachung
	this._map.on(gc.gis.map.GCMapEvent.LAYER_ADDED, this, function (evt) {
		if (evt.layers)
		{
			var lyr, i;
			for (i = 0; i < evt.layers.length; i++)
			{
				lyr = evt.layers[i];
				if (lyr instanceof gc.gis.layer.GCLayer && lyr.isTooltipEnabled()
					|| lyr instanceof gc.gis.layer.GCGroupLayer)
				{
					lyr.on(gc.gis.layer.GCLayerEvent.VISIBILITY_CHANGED, self, function(evt) {
						self._updateLayerList();
					});
				}
			}
		}
		self._updateLayerList();
	});
	this._map.on(gc.gis.map.GCMapEvent.LAYER_REMOVED, this, function (evt) {
		if (evt.layers)
		{
			var lyr, i;
			for (i = 0; i < evt.layers.length; i++)
			{
				lyr = evt.layers[i];
				if (lyr instanceof gc.gis.layer.GCLayer && lyr.isTooltipEnabled()
					|| lyr instanceof gc.gis.layer.GCGroupLayer)
				{
					lyr.un(gc.gis.layer.GCLayerEvent.VISIBILITY_CHANGED, self);
				}
			}
		}
		self._updateLayerList();
	});
	this._map.on(gc.gis.map.GCMapEvent.LAYER_MOVED, this, function (evt) {
		self._updateLayerList();
	});

	// Tooltip-Handling
	this._service.on(gc.gis.service.FeatureServiceEvent.HTML_POPUP_START, this, function (evt) {
		this._loadInfo ? self._map.showMouseTooltip(this._loadInfo) : self._map.disableMouseTooltip();
	});
	this._service.on(gc.gis.service.FeatureServiceEvent.HTML_POPUP_SUCCESS, this, function (evt) {
		if (evt.htmlPopup && evt.htmlPopup.length > 0)
		{
			self._map.showMouseTooltip(evt.htmlPopup);
		}
		else
		{
			self._map.disableMouseTooltip();
		}
	});
	this._service.on(gc.gis.service.FeatureServiceEvent.HTML_POPUP_ERROR, this, function (evt) {
		self._map.disableMouseTooltip();
	});
};

/**
 * Startet die Tooltipfunktionalität.
 *
 * @private
 */
gc.gis.control.GCLayerTooltipManager.prototype._update = function()
{
	if (this._hoverMap && this._interactionCount === 0 && this._layers.length > 0)
	{
		if (!this._active)
		{
			this._map.on(gc.gis.map.GCMapEvent.POINTER_MOVE, this, function (evt) {
				this._handlePointerMove(evt);
			});
			this._active = true;
		}
	}
	else
	{
		if (this._active)
		{
			this._map.un(gc.gis.map.GCMapEvent.POINTER_MOVE, this);
			this._active = false;
		}
	}
};

/**
 * Aktualisiert die Liste der Tooltip-Layer.
 *
 * @private
 */
gc.gis.control.GCLayerTooltipManager.prototype._updateLayerList = function()
{
	this._layers = this._map.getLayersRecursive(function(layer) {
		return layer.isReallyVisible()
			&& layer instanceof gc.gis.layer.GCLayer
			&& layer.isInternal()
			&& layer.isTooltipEnabled();
	});
};

/**
 * Ereignisbehandlungsroutine, die beim Bewegen des Mauszeigers ausgeführt wird.
 *
 * @param evt Browser-Event
 * @private
 */
gc.gis.control.GCLayerTooltipManager.prototype._handlePointerMove = function(evt)
{
	if (!this._mustTimerStart(evt.pixel))
	{
		return;
	}

	this._pixel = evt.pixel;
	this._map.disableMouseTooltip();
	this._service.cancelCurrentRequest();

	if (this._timer)
	{
		clearTimeout(this._timer);
	}

	var self = this;
	this._timer = setTimeout(function () {
		self._showTooltip(evt.coordinate[0], evt.coordinate[1])
	}, this._delay);
};

/**
 * Veranlasst das Laden der Tooltipinhalte und deren Anzeige.
 *
 * @param {double} x
 * @param {double} y
 * @private
 */
gc.gis.control.GCLayerTooltipManager.prototype._showTooltip = function(x, y)
{
	if (this._layers.length === 0)
	{
		return;
	}

	var point = gc.gis.iface.mapFactory.createPoint();
	point.setXY(x, y);
	point.setCRS(this._map.getProjection());

	this._service.htmlPopup(
		this._layers,
		point,
		this._map.getViewportExtent(),
		this._map.getSize()
	);
};

/**
 * Prüft, ob ein neuer Timer gestartet werden muss.
 *
 * @param {Array.<number>} pixel
 * @return {boolean}
 * @private
 */
gc.gis.control.GCLayerTooltipManager.prototype._mustTimerStart = function(pixel)
{
	if (!this._pixel)
	{
		return true;
	}

	return Math.abs(this._pixel[0] - pixel[0]) >= this._pixelThreshold ||
		Math.abs(this._pixel[1] - pixel[1]) >= this._pixelThreshold;
};
